import { Page } from "../../../components/Page";
import { useTranslation } from "react-i18next";
import ISSBImage from "../../../assets/img/issb/ISSB-action-plan.png";
import { Box, Button, chakra, Flex, IconButton, Image, Text, Tooltip, useBreakpointValue } from "@chakra-ui/react";
import * as React from "react";
import { EsgLabel, FormInput, FormMultiSelect, FormSelect } from "../../../components/Forms";
import { useForm } from "react-hook-form";
import { VALIDATION_PATTERNS } from "../../../utils";
import { FaExclamationCircle } from "react-icons/fa";
import { renderOptions } from "../../OrganizationQuestionnaire/components/Categories/components/Category/helpers";
import { useGetIssbConfiguration } from "../../../hooks/query";
import { useSearchParams } from "react-router-dom";

function CompanyDetails({ handleCreatePlan }) {
  const { t, i18n } = useTranslation("translation", {
    keyPrefix: "issbPlan.companyDetails"
  });
  const { t: globalTranslation } = useTranslation("translation");
  let [searchParams] = useSearchParams();

  const containerFlow = useBreakpointValue({
    base: "column",
    xl: "row"
  });

  const itemContainerFlow = useBreakpointValue({
    base: "column",
    lg: "row"
  });

  const {
    data: configurationData
  } = useGetIssbConfiguration();

  const {
    register,
    formState: { errors },
    watch,
    resetField,
    control,
    handleSubmit
  } = useForm({
    defaultValues: {
      email: searchParams?.get("email") ?? ""
    }
  });

  const formValues = watch();

  const companyIndustrySelector = register("companyIndustry", {
    required: globalTranslation("entityRequired", { entityName: t("labels.companyIndustry") })
  });

  const prepareSubmitData = (data) => {
    handleCreatePlan({
      ...data,
      subIndustries: data.subIndustries.map(sub => sub.value),
      companyRevenue: data.companyRevenue !== "" ? data.companyRevenue : null,
      companyStaff: data.companyStaff !== "" ? data.companyStaff : null
    });
  };

  return (
    <Page title={t("page.title")}>
      <Flex
        gap={8}
        marginTop={10}
        marginBottom={10}
        flexFlow={containerFlow}
        alignItems="center"
        justifyContent="space-between"
      >
        <Box width={containerFlow === "row" ? "55%" : "100%"}>
          <Text fontSize="xl" fontWeight="bold" marginBottom={4}>
            {t("labels.refresher")}:
          </Text>
          <Text fontSize="md" fontWeight={500} marginBottom={10}>
            {t("labels.requirements")}
          </Text>
          <Text backgroundColor={"#CBD5E0"} padding={5} borderRadius={8} fontWeight={500}>
            {t("labels.toolExplanation")}
          </Text>
        </Box>
        <Image
          src={ISSBImage}
          width={containerFlow === "row" ? "40%" : "100%"}
          maxWidth={containerFlow === "column" ? 500 : null}
        />
      </Flex>
      {configurationData ?
        <Box
          backgroundColor="gray.100"
          borderRadius={16}
          paddingBlock={14}
          paddingInline={containerFlow === "row" ? 36 : 14}
        >
          <Text fontSize="xl" fontWeight="bold" marginBottom={4}>
            {t("labels.formHeader")}:
          </Text>
          <chakra.form id="createPlan" onSubmit={handleSubmit(prepareSubmitData)}>
            <Flex flexDirection="column" gap={4}>
              <Flex
                flexFlow={itemContainerFlow}
                justifyContent="space-between"
                alignItems={itemContainerFlow === "row" ? "end" : null}
                gap={2}
              >
                <Box width={itemContainerFlow === "row" ? "40%" : "100%"}>
                  <EsgLabel text={`${t("labels.name")} *`} fontWeight={500} />
                  <FormInput
                    errors={errors}
                    {...register("name", {
                      required: globalTranslation("entityRequired", { entityName: t("labels.name") })
                    })}
                    title={formValues["name"] ?? ""}
                    maxInlineSize={"var(--chakra-sizes-full)"}
                  />
                </Box>
                <Box width={itemContainerFlow === "row" ? "40%" : "100%"}>
                  <Flex alignItems="center">
                    <EsgLabel text={`${t("labels.email")} *`} fontWeight={500} />
                    <Tooltip
                      label={t("labels.emailTooltip")}
                      whiteSpace={"pre-line"}
                      hasArrow
                    >
                      <IconButton
                        aria-label="Exclamation icon button"
                        type="button"
                        icon={<FaExclamationCircle size="20" />}
                        flexShrink="0"
                        size="sm"
                        opacity="0.25"
                        tabIndex={-1}
                      />
                    </Tooltip>
                  </Flex>
                  <FormInput
                    type="email"
                    errors={errors}
                    {...register("email",
                      {
                        required: globalTranslation("entityRequired", { entityName: t("labels.email") }),
                        pattern: VALIDATION_PATTERNS.EMAIL_ADDRESS
                      })}
                    title={formValues["email"] ?? ""}
                    maxInlineSize={"var(--chakra-sizes-full)"}
                  />
                </Box>
              </Flex>
              <Flex flexFlow={itemContainerFlow} justifyContent="space-between" gap={2}>
                <Box width={itemContainerFlow === "row" ? "40%" : "100%"}>
                  <EsgLabel text={`${t("labels.companyName")} *`} fontWeight={500} />
                  <FormInput
                    errors={errors}
                    {...register("companyName", {
                      required: globalTranslation("entityRequired", { entityName: t("labels.companyName") })
                    })}
                    title={t("labels.companyName")}
                    maxInlineSize={"var(--chakra-sizes-full)"}
                  />
                </Box>
                <Box width={itemContainerFlow === "row" ? "40%" : "100%"}>
                  <EsgLabel text={t("labels.companyRevenue")} fontWeight={500} />
                  <FormSelect
                    errors={errors}
                    {...register("companyRevenue")}
                    placeholder=" "
                    title={formValues["companyRevenue"] !== "" ?
                      t(`options.companyRevenue.${formValues["companyRevenue"]}`) : ""}
                    maxInlineSize={"var(--chakra-sizes-full)"}
                  >
                    {renderOptions(configurationData.revenueOptions ?? [],
                      "issbPlan.companyDetails.options.companyRevenue")}
                  </FormSelect>
                </Box>
              </Flex>
              <Flex flexFlow={itemContainerFlow} justifyContent="space-between" gap={2}>
                <Box width={itemContainerFlow === "row" ? "40%" : "100%"}>
                  <EsgLabel text={t("labels.companyStaff")} fontWeight={500} />
                  <FormSelect
                    errors={errors}
                    {...register("companyStaff")}
                    placeholder=" "
                    title={formValues["companyStaff"] !== "" ?
                      t(`options.companyStaff.${formValues["companyStaff"]}`) : ""}
                    maxInlineSize={"var(--chakra-sizes-full)"}
                  >
                    {renderOptions(configurationData.staffOptions ?? [],
                      "issbPlan.companyDetails.options.companyStaff")}
                  </FormSelect>
                </Box>
                <Box width={itemContainerFlow === "row" ? "40%" : "100%"}>
                  <EsgLabel text={`${t("labels.companyIndustry")} *`} fontWeight={500} />
                  <FormSelect
                    errors={errors}
                    {...companyIndustrySelector}
                    onChange={(e) => {
                      companyIndustrySelector.onChange(e);
                      resetField("subIndustries", { defaultValue: "" });
                    }}
                    placeholder=" "
                    title={formValues["companyIndustry"] && formValues["companyIndustry"] !== "" ?
                      t(`options.companyIndustry.${formValues["companyIndustry"]}`) : ""}
                    maxInlineSize={"var(--chakra-sizes-full)"}
                  >
                    {renderOptions(Object.keys(configurationData.industries?.industryToSubIndustry)?.sort() ?? [],
                      "issbPlan.companyDetails.options.companyIndustry")}
                  </FormSelect>
                </Box>
              </Flex>
              <Flex flexFlow={itemContainerFlow} justifyContent="space-between" gap={4}>
                <Box width={itemContainerFlow === "row" ? "40%" : "100%"}>
                  <EsgLabel text={`${t("labels.subIndustries")} *`} fontWeight={500} />
                  <FormMultiSelect
                    control={control}
                    name="subIndustries"
                    id="subIndustries"
                    options={formValues["companyIndustry"] && formValues["companyIndustry"] !== "" ?
                      configurationData.industries?.industryToSubIndustry[formValues["companyIndustry"]]?.map(sub => {
                        return {
                          label: i18n.exists(`issbPlan.companyDetails.options.subIndustries.${formValues["companyIndustry"]}.${sub.name}`) ?
                            globalTranslation(`issbPlan.companyDetails.options.subIndustries.${formValues["companyIndustry"]}.${sub.name}`) : sub.name,
                          value: sub.id
                        };
                      })?.sort((a, b) => {
                        return a.label.toLowerCase().localeCompare(b.label.toLowerCase());
                      }) ?? [] : []}
                    placeholder=" "
                    rules={{ required: globalTranslation("entityRequired", { entityName: t("labels.subIndustriesShort") }) }}
                    errors={errors}
                    chakraStyles={{
                      control: (provided) => ({
                        ...provided,
                        borderRadius: "none",
                        backgroundColor: "white"
                      })
                    }}
                  />
                </Box>
                <Flex alignItems="end" width={itemContainerFlow === "row" ? "40%" : "100%"} justifyContent="end">
                  <Button
                    type="submit"
                    colorScheme="customScheme"
                    width="40"
                    borderRadius={6}
                  >
                    {t("labels.createMyPlanBtn")}
                  </Button>
                </Flex>
              </Flex>
            </Flex>
          </chakra.form>
        </Box> : null}
    </Page>
  );
}

export default CompanyDetails;